import { useState, useEffect, useRef } from "react";
import {
  getContactList,
  getContactStatus,
} from "../../../service/Contacts.service";
import { CONST } from "../../../consts/consts";
import NeoTooltip from "../../../design/design_components/neo/overlay/NeoTooltip.base";
import NeoRangeNumberFilter from "../../../design/design_components/neo/table/NeoRangeNumberFilter.base";
import NeoDateRangeFilter from "../../../design/design_components/neo/table/NeoDateRangeFilter.base";
import NeoMultiSelectFilter from "../../../design/design_components/neo/table/NeoMultiSelectFilter.base";
import NeoTable from "../../../design/design_components/neo/table/NeoTable.base";
import NeoTableColumn from "../../../design/design_components/neo/table/NeoTableColumn.base";
import useToastContext from "../../../hooks/useToastContext.hook";
import Routes from "../../../routers/Routes.router.json";
import { useHistory } from "react-router-dom";
import moment from "moment-timezone";
import { auth } from "../../../config/firebase.config";
import { hideEmail } from "../../../utils/commons.util";

export default function ContactsTable() {
  const ROWS_COUNT = 10;
  const EMAIL_VALIDATION_COLLECTIONS = Object.entries(CONST.EMAIL_VALIDATIONS).map(
    (type) => type[1]
  );
  const [contactStatusCollection, setContactStatusCollection] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [data, setData] = useState([]);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: ROWS_COUNT,
    page: 1,
    sortField: "last_visit_at",
    sortOrder: -1,
  });

  const toast = useToastContext();
  const tableRef = useRef(null);
  const history = useHistory(null);
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    getContactStatus().then((contactsStatusCollection) => {
      if (contactsStatusCollection.success && isMounted.current) {
        setContactStatusCollection(
          contactsStatusCollection.result.contactStatus.map(({ STATUS_NAME }) => ({
            label: STATUS_NAME,
            value: STATUS_NAME,
          }))
        );
      }
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    getContactList(lazyParams).then((contactsData) => {
      if (contactsData.success && isMounted.current) {
        setData(contactsData.payload.contacts);
        setTotalRecords(contactsData.payload.totalRecords);
      }
    }).finally(() => {
      setLoading(false);
    });
  }, [lazyParams]);

  const goToContactDetail = (contactData) => {
    const { uuid } = contactData;
    history.push(`${Routes.contacts.detailsWithOutParams}/${uuid}`, contactData);
  };

  const emailValidationQuestionMark = () => {
    return `
       ${CONST.EMAIL_VALIDATIONS.valid.label}: ${CONST.EMAIL_VALIDATIONS.valid.description}\n
        ${CONST.EMAIL_VALIDATIONS.invalid.label}: ${CONST.EMAIL_VALIDATIONS.invalid.description}\n
        ${CONST.EMAIL_VALIDATIONS.risky.label}: ${CONST.EMAIL_VALIDATIONS.risky.description}\n
        ${CONST.EMAIL_VALIDATIONS.business.label}:
        ${CONST.EMAIL_VALIDATIONS.business.description}\n
        ${CONST.EMAIL_VALIDATIONS.unknown.label}:
        ${CONST.EMAIL_VALIDATIONS.unknown.description}\n
        ${CONST.EMAIL_VALIDATIONS.noValid.label}:
        ${CONST.EMAIL_VALIDATIONS.noValid.description}\n
      `;
  };

  return (
    <div>
      <div className="content-section implementation">
        <NeoTooltip target=".emailValidation" data-pr-showdelay={1000} />
        <div className="card">
          <NeoTable
            emptyMessage={"No hay contactos registrados"}
            ref={tableRef}
            value={data}
            lazy
            selectionMode="single"
            dataKey="uuid"
            autoLayout={true}
            paginator
            first={lazyParams.first}
            rows={ROWS_COUNT}
            totalRecords={totalRecords}
            removableSort
            sortField={lazyParams.sortField}
            sortOrder={lazyParams.sortOrder}
            onPage={(event) => setLazyParams((lazyParams) => { return { ...lazyParams, ...event } })}
            onSort={(event) => setLazyParams((lazyParams) => { return { ...lazyParams, ...event } })}
            onFilter={(event) => setLazyParams((lazyParams) => { return { ...lazyParams, ...event } })}
            filters={lazyParams.filters}
            loading={loading}
            onRowSelect={(e) => goToContactDetail(e.data)}
            filterDelay={1000}
          >
            <NeoTableColumn
              header="Correo electrónico"
              field="email"
              filter
              filterMatchMode="contains"
              filterPlaceholder="Buscar por correo"
              sortable
            />
            <NeoTableColumn
              header={
                <>
                  <NeoTooltip
                    target=".emailValidation"
                    data-pr-showdelay={1000}
                  />
                  Validación del correo
                  <i
                    className="pi pi-question-circle emailValidation"
                    data-pr-tooltip={emailValidationQuestionMark()}
                  ></i>
                </>
              }
              field="email_validation"
              filter
              filterElement={
                <NeoMultiSelectFilter
                  ref={tableRef}
                  options={EMAIL_VALIDATION_COLLECTIONS}
                  field={"email_validation"}
                  placeholder="Todos"
                  selectedItemsLabel="{0} tipos de validaciones"
                />
              }
              sortable
            />
            <NeoTableColumn
              header="Última conexión"
              field="last_visit_at"
              filter
              filterElement={
                <NeoDateRangeFilter
                  ref={tableRef}
                  field={"last_visit_at"}
                  placeholder={"Rango de fechas"}
                  matchFilter="range"
                />
              }
              filterMatchMode="range"
              dataType="date"
              body={(row) => {
                const { last_visit_at } = row;
                if (Boolean(last_visit_at) === false) return null;
                return Intl.DateTimeFormat(undefined, {
                  month: "long",
                  hour12: false,
                  hour: "2-digit",
                  minute: "2-digit",
                  year: "numeric",
                  day: "numeric",
                }).format(moment(last_visit_at).toDate())
              }}
              sortable
            />
            <NeoTableColumn
              header="Cantidad de visitas"
              field="visit_count"
              filter
              filterElement={
                <NeoRangeNumberFilter
                  ref={tableRef}
                  field={"visit_count"}
                  minPlaceholder={"Mín"}
                  maxPlaceholder={"Máx"}
                  minDigit={0}
                />
              }
              filterMatchMode="contains"
              sortable
            />
            <NeoTableColumn
              header="Estatus"
              field="ContactStatus.status_name"
              filter
              filterElement={
                <NeoMultiSelectFilter
                  ref={tableRef}
                  options={contactStatusCollection}
                  field={"ContactStatus.status_name"}
                  placeholder="Todos"
                  selectedItemsLabel="{0} tipos de estatus"
                />
              }
              filterMatchMode="contains"
              sortable
            />
          </NeoTable>
        </div>
      </div>
    </div>
  );
}
